import { Component, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CapitalContainer } from '@commons/capital-container/capital-container';
import { CapitalBack } from '@commons/capital-back/capital-back';
import { CapitalCard } from '@commons/capital-card/capital-card';
import { CapitalInput } from '@commons/capital-input/capital-input';
import { CapitalSelect } from '@commons/capital-select/capital-select';
import { PrimaryButton } from '@commons/primary-button/primary-button';
import { AuthService, DatosPersonales } from '@commons/services/auth.service';
import { formatRutWithDots } from '@utils/rut.utils';
import ComunasRegionesData from '../../utils/ComunasRegionesChile.json';

interface ComunaData {
  comuna: string;
  codigo: number;
  ciudad: string;
}

interface RegionData {
  nombreRegion: string;
  comunas: ComunaData[];
}

type RegionesChile = Record<string, RegionData>;

@Component({
  selector: 'app-update-personal-data',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CapitalContainer,
    CapitalBack,
    CapitalCard,
    CapitalInput,
    CapitalSelect,
    PrimaryButton
  ],
  templateUrl: './update-personal-data.html',
  styleUrl: './update-personal-data.scss',
})
export class UpdatePersonalDataComponent implements OnInit {
  updateForm: FormGroup;
  isLoading = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  successMessage = signal<string | null>(null);
  errorMessage = signal<string | null>(null);
  
  // Datos de regiones y comunas de Chile
  private readonly regionesData: RegionesChile = ComunasRegionesData as RegionesChile;
  
  // Mapeo entre nombre corto y nombre completo de región
  private readonly regionesMap = new Map<string, string>();
  
  // Opciones para los selects
  readonly paises = signal(['Chile']);
  readonly regiones = signal<string[]>([]); // Nombres cortos para el select
  readonly regionesCompletas = signal<string[]>([]); // Nombres completos para el dropdown
  readonly comunas = signal<string[]>([]);
  readonly ciudades = signal<string[]>([]);
  readonly regionPlaceholder = signal<string>('Región');

  constructor(
    private readonly fb: FormBuilder,
    public readonly authService: AuthService
  ) {
    this.updateForm = this.fb.group({
      correo: ['', [Validators.required, Validators.email]],
      telefonoMovil: ['', Validators.required],
      telefonoFijo: [''],
      paisResidencia: ['Chile', Validators.required],
      region: ['', Validators.required],
      comuna: ['', Validators.required],
      ciudad: ['', Validators.required],
      direccion: ['', Validators.required],
      // Campos de solo lectura
      nombre: [{value: '', disabled: true}],
      apellidos: [{value: '', disabled: true}],
      rut: [{value: '', disabled: true}],
      fechaNacimiento: [{value: '', disabled: true}],
      sexo: [{value: '', disabled: true}],
      estadoCivil: [{value: '', disabled: true}]
    });

    // Cargar lista de regiones al iniciar
    this.loadRegiones();

    // Escuchar cambios en la región para actualizar comunas
    this.updateForm.get('region')?.valueChanges.subscribe((regionNombre: string) => {
      this.onRegionChange(regionNombre);
    });
  }

  ngOnInit(): void {
    console.log('ngOnInit - Datos actuales:', this.authService.datosPersonales());
    console.log('ngOnInit - isLoadingDatos:', this.authService.isLoadingDatos());
    
    // Si ya hay datos cargados, cargarlos en el formulario
    if (this.authService.datosPersonales() !== null) {
      console.log('Ya hay datos, cargando en formulario...');
      this.loadDataIntoForm(this.authService.datosPersonales()!);
      return;
    }
    
    // Si ya se está cargando, esperar a que termine
    if (this.authService.isLoadingDatos()) {
      console.log('Ya se están cargando los datos, esperando...');
      this.isLoading.set(true);
      this.waitForDataLoad();
      return;
    }
    
    // Si no hay datos y no se están cargando, cargarlos
    console.log('No hay datos, iniciando carga...');
    this.isLoading.set(true);
    this.authService.loadDatosPersonales()
      .then((datos) => {
        console.log('Datos cargados exitosamente:', datos);
        this.loadDataIntoForm(datos);
        this.isLoading.set(false);
      })
      .catch((error) => {
        console.error('Error al cargar datos:', error);
        this.isLoading.set(false);
      });
  }

  /**
   * Espera a que termine la carga de datos en progreso
   */
  private waitForDataLoad(): void {
    // Usar effect para escuchar cambios en isLoadingDatos
    const checkInterval = setInterval(() => {
      if (!this.authService.isLoadingDatos()) {
        clearInterval(checkInterval);
        const datos = this.authService.datosPersonales();
        if (datos) {
          console.log('Datos disponibles después de esperar:', datos);
          this.loadDataIntoForm(datos);
        } else {
          console.error('No se pudieron cargar los datos');
        }
        this.isLoading.set(false);
      }
    }, 100); // Verificar cada 100ms
  }

  private loadDataIntoForm(datos: DatosPersonales) {
    console.log('loadDataIntoForm - Datos recibidos:', datos);
    
    // Formatear fecha de nacimiento al formato dd/mm/aaaa
    let fechaNacimiento = datos.fechaNacimiento;
    if (fechaNacimiento) {
      // El formato viene como DD-MM-YYYY, convertir a dd/mm/aaaa
      fechaNacimiento = fechaNacimiento.replaceAll('-', '/');
    }

    // Formatear RUT con puntos
    const rutFormateado = formatRutWithDots(datos.rut);

    // Formatear teléfonos con código de área
    const telefonoMovil = datos.telMovil ? `+569 ${datos.telMovil}` : '';
    const telefonoFijo = datos.telFijo ? `+569 ${datos.telFijo}` : '';

    // Obtener nombre completo de región desde el código
    const nombreRegionCompleto = datos.codigoRegion 
      ? this.getNombreRegionByCodigo(datos.codigoRegion)
      : datos.region || '';

    // Extraer nombre corto para mostrar en el input
    const nombreRegionCorto = nombreRegionCompleto 
      ? this.extraerNombreCorto(nombreRegionCompleto)
      : '';

    // Cargar comunas de la región seleccionada
    if (nombreRegionCorto) {
      this.onRegionChange(nombreRegionCorto);
    }

    // Cargar datos modificables
    this.updateForm.patchValue({
      correo: datos.email || '',
      telefonoMovil: telefonoMovil,
      telefonoFijo: telefonoFijo,
      paisResidencia: datos.paisResidencia || 'Chile',
      comuna: datos.comuna || '',
      ciudad: datos.ciudad || '',
      direccion: datos.direccion || ''
    }, { emitEvent: false });

    // Actualizar región después para forzar la detección de cambios
    setTimeout(() => {
      this.updateForm.patchValue({
        region: nombreRegionCorto
      }, { emitEvent: false });
    }, 0);

    // Para los campos deshabilitados, usar setValue directamente
    this.updateForm.get('nombre')?.setValue(datos.nombres || '');
    this.updateForm.get('apellidos')?.setValue(
      `${datos.apellidoPaterno || ''} ${datos.apellidoMaterno || ''}`.trim()
    );
    this.updateForm.get('rut')?.setValue(rutFormateado);
    this.updateForm.get('fechaNacimiento')?.setValue(fechaNacimiento || '');
    this.updateForm.get('sexo')?.setValue(datos.sexo || 'No especificado');
    this.updateForm.get('estadoCivil')?.setValue('No especificado');

    console.log('Formulario después de cargar datos:', this.updateForm.value);
  }

  /**
   * Carga la lista de todas las regiones
   */
  private loadRegiones(): void {
    const regionesCompletasArray: string[] = [];
    const regionesCortasArray: string[] = [];
    
    Object.values(this.regionesData).forEach(r => {
      const nombreCompleto = r.nombreRegion;
      const nombreCorto = this.extraerNombreCorto(nombreCompleto);
      
      regionesCompletasArray.push(nombreCompleto);
      regionesCortasArray.push(nombreCorto);
      
      // Guardar mapeo
      this.regionesMap.set(nombreCorto, nombreCompleto);
      this.regionesMap.set(nombreCompleto, nombreCorto);
    });
    
    this.regionesCompletas.set(regionesCompletasArray);
    this.regiones.set(regionesCortasArray);
  }

  /**
   * Extrae el nombre corto de una región
   * Ejemplo: "Región de Valparaíso" -> "Valparaíso"
   * Ejemplo: "Región Metropolitana de Santiago" -> "Metropolitana"
   */
  private extraerNombreCorto(nombreCompleto: string): string {
    // Caso especial: Región Metropolitana
    if (nombreCompleto.includes('Metropolitana')) {
      return 'Metropolitana';
    }
    // Remover "Región de ", "Región del ", etc.
    return nombreCompleto
      .replace(/^Región\s+(de|del|de\s+la|de\s+los|de\s+las)\s+/i, '')
      .trim();
  }

  /**
   * Obtiene el placeholder dinámico basado en el nombre corto
   */
  private obtenerPlaceholderRegion(nombreCorto: string): string {
    if (!nombreCorto) return 'Región';
    
    // Caso especial: Región Metropolitana
    if (nombreCorto === 'Metropolitana') {
      return 'Región';
    }
    
    const nombreCompleto = this.regionesMap.get(nombreCorto);
    if (!nombreCompleto) return 'Región';
    
    // Extraer la preposición
    const match = nombreCompleto.match(/^Región\s+(de|del|de\s+la|de\s+los|de\s+las)\s+/i);
    return match ? `Región ${match[1]}` : 'Región';
  }

  /**
   * Maneja el cambio de región y actualiza las comunas disponibles
   */
  private onRegionChange(regionNombre: string): void {
    if (!regionNombre) {
      this.comunas.set([]);
      this.ciudades.set([]);
      this.regionPlaceholder.set('Región');
      // Limpiar comuna y ciudad cuando no hay región
      this.updateForm.patchValue({
        comuna: '',
        ciudad: ''
      }, { emitEvent: false });
      return;
    }

    // Actualizar placeholder
    this.regionPlaceholder.set(this.obtenerPlaceholderRegion(regionNombre));

    // Obtener nombre completo si se recibe nombre corto
    const nombreCompleto = this.regionesMap.get(regionNombre) || regionNombre;

    // Buscar el código de región por nombre completo
    const regionEntry = Object.entries(this.regionesData).find(
      ([_, data]) => data.nombreRegion === nombreCompleto
    );

    if (regionEntry) {
      const [_, regionData] = regionEntry;
      const comunasArray = regionData.comunas.map(c => c.comuna);
      const ciudadesArray = [...new Set(regionData.comunas.map(c => c.ciudad))];
      
      // Agregar opción vacía al inicio para evitar selección automática
      this.comunas.set(['', ...comunasArray]);
      this.ciudades.set(['', ...ciudadesArray]);
      
      // Limpiar comuna y ciudad cuando cambia la región
      // Verificar si la comuna actual existe en la nueva lista
      const comunaActual = this.updateForm.get('comuna')?.value;
      if (comunaActual && !comunasArray.includes(comunaActual)) {
        this.updateForm.patchValue({
          comuna: '',
          ciudad: ''
        }, { emitEvent: false });
      }
    }
  }

  /**
   * Busca el nombre de región por su código
   */
  private getNombreRegionByCodigo(codigoRegion: number): string {
    console.log('Buscando región con código:', codigoRegion);
    console.log('Claves disponibles en regionesData:', Object.keys(this.regionesData));
    const regionData = this.regionesData[codigoRegion.toString()];
    console.log('Datos de región encontrados:', regionData);
    return regionData?.nombreRegion || '';
  }

  async onSubmit() {
    if (!this.updateForm.valid) {
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    try {
      const formValue = this.updateForm.getRawValue();
      const datosActuales = this.authService.datosPersonales();
      
      if (!datosActuales) {
        throw new Error('No hay datos personales disponibles');
      }

      // Limpiar teléfonos (remover +569 y espacios)
      const telefonoMovil = formValue.telefonoMovil
        ? formValue.telefonoMovil.replace(/^\+569?\s*/, '').trim()
        : '';
      
      const telefonoFijo = formValue.telefonoFijo
        ? formValue.telefonoFijo.replace(/^\+569?\s*/, '').trim()
        : '';

      // Preparar datos para enviar al backend según la documentación del endpoint
      // Mapeo: teléfono móvil -> celular, teléfono fijo -> telefono
      const datosNuevos = {
        nombre: datosActuales.nombres,
        apellidoPaterno: datosActuales.apellidoPaterno,
        apellidoMaterno: datosActuales.apellidoMaterno,
        telefono: telefonoFijo,
        celular: telefonoMovil,
        email: formValue.correo,
        direccion: formValue.direccion,
        comuna: formValue.comuna
      };

      console.log('Enviando datos al backend:', datosNuevos);

      // Llamar al servicio de actualización
      const response = await this.authService.updateDatosPersonales(datosNuevos);
      
      this.successMessage.set(response.message || 'Datos actualizados correctamente');
      
      // Limpiar mensaje de éxito después de 5 segundos
      setTimeout(() => {
        this.successMessage.set(null);
      }, 5000);

    } catch (error) {
      console.error('Error al actualizar datos:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error al actualizar los datos';
      this.errorMessage.set(errorMsg);
      
      // Limpiar mensaje de error después de 7 segundos
      setTimeout(() => {
        this.errorMessage.set(null);
      }, 7000);
    } finally {
      this.isSaving.set(false);
    }
  }
}
